import { transparentize } from 'polished'

import { amber, clay, fern, indigo, moss, rust } from './colorPrimitives'

// Primary
export const white = '#FFFFFF'
export const darkGreen = fern[700]

// Secondary
export const red = rust[500]
export const yellow = amber[500]

// Tertiary
export const aquamarine = indigo[300]
export const blue = indigo[500]
export const purple = '#8886E9'

export const transparent = 'transparent'

// Kept for backward compat (theme.colors.blueShade30); pending deprecation.
export const blueShade30 = '#30789F'

// GreyScale
const whiteShade75 = '#404040' // shade(0.75, white)
const whiteShade70 = '#4D4D4D' // shade(0.70, white)
const whiteShade60 = '#666666' // shade(0.6, white)
const whiteShade50 = '#808080' // shade(0.5, white)
const whiteShade40 = '#999999' // shade(0.4, white)
const whiteShade30 = '#B3B3B3' // shade(0.3, white)
const whiteShade20 = '#CCCCCC' // shade(0.2, white)
const whiteShade10 = '#E0E0E0' // shade(0.1, white)
const whiteShade5 = '#F2F2F2' // shade(0.05, white)
const whiteShade2 = '#FAFAFA' // shade(0.02, white)

// LEGACY Greys
export const darkGrey = whiteShade75
export const lightGrey = '#BFBFBF'
export const offwhite = clay[50]
const disabledGrey = '#E3E3E3'

// LEGACY TOKENS - Content
export const background = clay[50]
export const contentBackground = white

// LEGACY TOKENS - Text
export const textPrimary = clay[900]
export const textSecondary = whiteShade75
export const textTertiary = whiteShade40
export const textInverse = white
export const textDisabled = lightGrey
export const textLink = fern[600]

// LEGACY TOKENS - Progress
export const progressPositive = moss[200]
export const progressNegative = rust[200]

// Contextual
export const primary = fern[700]
export const secondary = amber[500]
export const selected = clay[100]
export const success = moss[500]
export const danger = rust[500]
export const warning = amber[500]
export const caution = amber[500]
export const info = indigo[500]
export const disabled = lightGrey
export const error = rust[500]
export const focus = indigo[500]
export const positive = moss[500]
export const ai = fern[600]
export const accent = indigo[500]
export const neutral = whiteShade5

// TOKENS - Content
export const content = {
  border: lightGrey,
  mainBackground: clay[50],
  contentBackground: white,
  errorBackground: rust[200],
  secondaryBackground: whiteShade2,
  secondaryBorder: whiteShade10,
  accentBackground: fern[50],
  accentBorder: fern[200],
}

// TOKENS - Text
export const text = {
  primary: clay[900],
  secondary: whiteShade75,
  tertiary: whiteShade40,
  inverse: white,
  disabled: lightGrey,
  placeholder: whiteShade75,
  link: fern[600],
  error: rust[500],
  highlight: indigo[500],
}

// TOKENS - Buttons
export const button = {
  base: {
    content: {
      default: text.primary,
      inverse: text.inverse,
      disabled: text.inverse,
    },
    background: {
      default: white,
      active: clay[200],
      hover: clay[50],
      disabled: clay[300],
    },
    border: {
      default: fern[700],
      disabled: clay[200],
    },
  },
  primary: {
    content: {
      default: white,
    },
    background: {
      default: fern[700],
      hover: fern[600],
      active: fern[800],
      disabled: clay[300],
    },
    border: {
      default: transparent,
    },
  },
  secondary: {
    content: {
      default: fern[700],
      disabled: clay[300],
    },
    background: {
      default: white,
      hover: clay[50],
    },
    border: {
      default: fern[700],
      hover: fern[700],
      active: white,
      disabled: clay[200],
    },
  },
  tertiary: {
    content: {
      default: text.link,
      hover: fern[700],
      active: fern[800],
      disabled: clay[300],
    },
    background: {
      default: white,
      hover: clay[50],
    },
    border: {
      default: transparent,
      disabled: transparent,
    },
  },
  request: {
    content: {
      default: fern[700],
      hover: fern[800],
      active: white,
    },
    background: {
      default: fern[100],
      hover: fern[200],
      active: fern[900],
    },
  },
  positive: {
    content: {
      default: white,
    },
    background: {
      default: fern[700],
      hover: fern[600],
      active: fern[800],
      disabled: clay[300],
    },
  },
  danger: {
    content: {
      default: white,
    },
    background: {
      default: rust[700],
      hover: rust[600],
      active: rust[800],
      disabled: clay[300],
    },
  },
  pending: {
    content: {
      default: white,
    },
    background: {
      default: amber[500],
      hover: amber[600],
    },
  },
  text: {
    content: {
      default: text.link,
      hover: fern[800],
      disabled: text.disabled,
    },
  },
  icon: {
    content: {
      default: fern[700],
      disabled: clay[300],
    },
    background: {
      default: white,
      hover: clay[50],
    },
    border: {
      default: fern[700],
      disabled: clay[200],
    },
  },
  transparentIcon: {
    content: {
      default: fern[600],
      hover: transparentize(0.5, whiteShade20),
    },
  },
  group: {
    content: {
      default: fern[700],
      active: white,
      disabled: clay[300],
    },
    background: {
      default: white,
      hover: clay[50],
      active: fern[800],
      activeHover: fern[700],
    },
    border: {
      default: fern[700],
      active: fern[700],
    },
  },
  filter: {
    content: {
      default: text.primary,
      disabled: clay[300],
    },
    background: {
      default: white,
      hover: clay[50],
      active: clay[200],
    },
    border: {
      default: clay[300],
    },
  },
}

// TOKENS - Navigation
export const navigation = {
  main: {
    background: clay[50],
  },
  link: {
    text: text.primary,
    focusOutline: indigo[500],
    chevronIcon: clay[700],
    backgroundHover: clay[50],
    backgroundActive: clay[100],
    backgroundOpen: white,
    backgroundCollapsedOpen: white,
  },
  linkContainer: {
    background: clay[50],
    expanderBorder: clay[700],
  },
  subLink: {
    text: text.primary,
    textHover: text.primary,
    background: white,
    backgroundHover: clay[50],
    backgroundActive: clay[100],
  },
  notificationIndicator: {
    background: darkGrey,
  },
}

// TOKENS - Form Fields
export const form = {
  field: {
    border: whiteShade20,
    borderActive: fern[700],
    borderError: rust[500],
    disabled: disabledGrey,
  },
  select: {
    optionBackgroundHover: clay[50],
    optionBackgroundActive: clay[100],
  },
  radioCardGroup: {
    border: content.border,
    activeBorder: fern[700],
    disabledBorder: whiteShade20,
  },
  datepicker: {
    border: content.border,
    backgroundDisabled: disabledGrey,
    borderError: rust[500],
    selected: fern[700],
  },
}

// TOKENS - Table
export const table = {
  hover: clay[50],
}

// TOKENS - Tabs
export const tabs = {
  contentActive: fern[700],
  contentInactive: whiteShade60,
  divider: lightGrey,
}

// TOKENS - Badge
export const badge = {
  success: {
    background: moss[100],
    content: moss[800],
  },
  ai: {
    background: fern[100],
    content: fern[800],
  },
  error: {
    background: rust[100],
    content: rust[800],
  },
  warning: {
    background: amber[100],
    content: amber[800],
  },
  inactive: {
    background: clay[100],
    content: clay[600],
  },
  incomplete: {
    background: clay[100],
    content: clay[800],
  },
  inProgress: {
    background: indigo[100],
    content: indigo[800],
  },
  highPriority: {
    background: clay[800],
    content: clay[50],
  },
  lowPriority: {
    background: clay[200],
    content: clay[800],
  },
  neutral: {
    background: clay[50],
    content: clay[800],
    border: clay[300],
  },
}

// TOKENS - Alert
export const alert = {
  error: {
    background: rust[50],
    border: rust[200],
    content: rust[900],
  },
  info: {
    background: indigo[50],
    border: indigo[200],
    content: indigo[900],
    icon: indigo[600],
  },
  warning: {
    background: amber[50],
    border: amber[200],
    content: amber[900],
  },
  ai: {
    background: fern[50],
    border: fern[200],
    content: fern[900],
    icon: fern[600],
  },
}

// TOKENS - Comments
export const comments = {
  others: {
    background: clay[50],
  },
  self: {
    background: clay[100],
  },
}

// TOKENS - Tooltip
export const tooltip = {
  background: clay[800],
  text: textInverse,
}

// TOKENS - Avatar
export const avatar = {
  self: {
    background: fern[600],
    text: clay[50],
  },
  others: {
    background: clay[200],
    text: clay[800],
  },
}

// TOKENS - Audit log
export const auditLog = {
  info: {
    background: indigo[200],
    text: indigo[800],
  },
  positive: {
    background: moss[200],
    text: moss[800],
  },
  negative: {
    background: amber[200],
    text: amber[800],
  },
}

// TOKENS - Budgets
export const budgets = {
  available: clay[200],
  committed: indigo[400],
  pending: amber[400],
  overBudget: rust[400],
}

// TOKENS - GlobalCommand
export const globalCommand = {
  text: clay[500],
  border: clay[300],
  accent: fern[100],
}
